import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { fmtNum } from "@/lib/format";
import {
  ArrowLeft, ArrowRight, Loader2, Check, AlertTriangle, RefreshCw, FileDown,
  Wand2, ShieldCheck, Sparkles, Clock, CheckCircle2, XCircle, Save, Replace,
  Users, UserPlus, Phone, Fingerprint, ChevronRight,
} from "lucide-react";

const HEALTH_COLORS = {
  Excellent: "text-[#16A34A]",
  Good: "text-[#0B3D91]",
  "Needs Attention": "text-amber-600",
  "Needs Work": "text-destructive",
};

const HEALTH_DOT = {
  Excellent: "bg-[#16A34A]",
  Good: "bg-[#0B3D91]",
  "Needs Attention": "bg-amber-500",
  "Needs Work": "bg-destructive",
};

function _uid() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return u?.user_id || u?.id || u?.email || "";
  } catch { return ""; }
}
const _healthKey = () => `recoveros_import_health_${_uid()}`;

// Persist the Import Health snapshot from the LAST validation, scoped to the import the user just ran.
// This is purely a UI breadcrumb — it never feeds dashboard calculations or the Recovery Health Score.
export function saveImportHealth(summary) {
  if (!summary) return;
  try {
    localStorage.setItem(_healthKey(), JSON.stringify({
      health: summary.health, label: summary.health_label,
      warnings: summary.warning_count, errors: summary.error_count, ts: Date.now(),
    }));
  } catch { /* noop */ }
}
export function readImportHealth() {
  try { return JSON.parse(localStorage.getItem(_healthKey()) || "null"); } catch { return null; }
}
function clearImportHealth() {
  try { localStorage.removeItem(_healthKey()); } catch { /* noop */ }
}

function healthMessage(h) {
  const w = h.warnings || 0;
  if (h.label === "Excellent") return `Your data quality is excellent.${w ? ` Only ${w} minor warning${w === 1 ? "" : "s"} ${w === 1 ? "was" : "were"} detected.` : " No issues were detected."}`;
  if (h.label === "Good") return `Your data quality is good.${w ? ` ${w} minor warning${w === 1 ? "" : "s"} noted for your review.` : ""}`;
  return `Your data imported successfully.${w ? ` ${w} warning${w === 1 ? "" : "s"} were flagged for review.` : ""}`;
}

// Shown on the import Done screen. Distinct from the Recovery Health Score (which measures receivables
// performance in the Money Stuck Analyzer) — this measures ONLY the quality of the uploaded file.
export function ImportHealthCard({ summary }) {
  if (!summary) return null;
  const color = HEALTH_COLORS[summary.health_label] || "text-foreground";
  const h = { health: summary.health, label: summary.health_label, warnings: summary.warning_count };
  return (
    <div className="mt-4 inline-flex items-start gap-3 border border-border bg-secondary/30 rounded-sm px-4 py-3 text-left max-w-md" data-testid="import-health-card">
      <ShieldCheck className={`h-5 w-5 mt-0.5 shrink-0 ${color}`} />
      <div>
        <div className="label-caps">Import Health</div>
        <div className={`text-lg font-black font-heading mono leading-tight ${color}`}>{summary.health}% · {summary.health_label}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{healthMessage(h)}</div>
      </div>
    </div>
  );
}

// Subtle, transient strip on the dashboard — only after an import, dismissible, cleared on next import.
// Intentionally muted so it never competes with the primary KPIs or the Recovery Health Score.
export function ImportHealthBadge() {
  const [data, setData] = useState(() => readImportHealth());
  if (!data) return null;
  const color = HEALTH_COLORS[data.label] || "text-foreground";
  const dot = HEALTH_DOT[data.label] || "bg-foreground";
  return (
    <div className="mb-4 flex items-center gap-2 rounded-sm border border-border bg-secondary/40 px-3 py-1.5 text-xs" data-testid="dashboard-import-health-badge">
      <span className={`h-2 w-2 rounded-full ${dot}`} />
      <span className="text-muted-foreground">Last import quality —</span>
      <span className={`font-semibold ${color}`}>Import Health {data.health}% · {data.label}</span>
      {data.warnings > 0 && <span className="text-muted-foreground hidden sm:inline">· {data.warnings} warning{data.warnings === 1 ? "" : "s"}</span>}
      <button type="button" onClick={() => { clearImportHealth(); setData(null); }} className="ml-1 text-muted-foreground hover:text-foreground" aria-label="Dismiss" data-testid="dismiss-import-health-badge">
        <XCircle className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function ScanningState() {
  return (
    <div className="border border-border bg-card p-12 text-center" data-testid="import-scanning">
      <div className="relative h-16 w-16 mx-auto">
        <div className="absolute inset-0 rounded-full border-2 border-[#0B3D91]/20" />
        <Loader2 className="h-16 w-16 animate-spin text-[#0B3D91]" strokeWidth={1.4} />
        <Sparkles className="h-6 w-6 text-[#16A34A] absolute inset-0 m-auto" />
      </div>
      <h3 className="font-heading font-bold text-lg mt-5">Scanning your data…</h3>
      <p className="text-sm text-muted-foreground mt-1">The Smart Import Assistant™ is checking every row so you don't have to.</p>
    </div>
  );
}

function ReassuringBanner({ summary }) {
  const { health, error_rows, warning_rows, total_rows, ready_rows } = summary;
  let msg, sub, tone;
  if (error_rows === 0 && warning_rows === 0) {
    tone = "bg-[#16A34A]/10 border-[#16A34A]/30 text-[#138a3e]";
    msg = "Your file is ready to import.";
    sub = `RecoverOS scanned all ${fmtNum(total_rows)} rows and everything looks clean.`;
  } else if (error_rows === 0) {
    tone = "bg-[#0B3D91]/10 border-[#0B3D91]/30 text-[#0B3D91]";
    msg = `Great! ${health}% of your data is ready.`;
    sub = `Only a few rows have minor warnings — you can import now or tidy them up below.`;
  } else {
    tone = "bg-amber-50 border-amber-200 text-amber-800";
    msg = `Most of your data is ready — ${fmtNum(ready_rows)} of ${fmtNum(total_rows)} rows are clean.`;
    sub = `Only ${error_rows} row${error_rows === 1 ? "" : "s"} need attention before import. You can fix them right here.`;
  }
  return (
    <div className={`border rounded-sm px-4 py-3 ${tone}`} data-testid="import-reassuring-banner">
      <div className="flex items-center gap-2 font-semibold text-sm"><ShieldCheck className="h-4 w-4" /> {msg}</div>
      <div className="text-xs mt-0.5 opacity-90">{sub}</div>
    </div>
  );
}

function HealthSummary({ summary }) {
  const healthColor = HEALTH_COLORS[summary.health_label] || "text-foreground";
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="import-health-summary">
      <Tile label="Total Rows" value={summary.total_rows} accent="text-foreground" testId="health-total" />
      <Tile label="Ready to Import" value={summary.ready_rows} accent="text-[#16A34A]" testId="health-ready" />
      <Tile label="Warnings" value={summary.warning_rows} accent="text-amber-600" testId="health-warnings" />
      <Tile label="Errors" value={summary.error_rows} accent="text-destructive" testId="health-errors" />
      <div className="border border-border bg-card p-4" data-testid="health-score">
        <div className="label-caps">Import Health</div>
        <div className={`text-2xl font-black font-heading mt-1 mono ${healthColor}`}>{summary.health}%</div>
        <div className={`text-xs font-semibold ${healthColor}`}>{summary.health_label}</div>
      </div>
    </div>
  );
}

function Tile({ label, value, accent, testId }) {
  return (
    <div className="border border-border bg-card p-4" data-testid={testId}>
      <div className="label-caps">{label}</div>
      <div className={`text-2xl font-black font-heading mt-1 mono ${accent || ""}`}>{fmtNum(value)}</div>
    </div>
  );
}

function HealthBar({ summary }) {
  const total = summary.total_rows || 1;
  const ready = summary.ready_rows;
  const warn = summary.warning_rows;
  const err = summary.error_rows;
  const pct = (n) => `${((n / total) * 100).toFixed(1)}%`;
  return (
    <div className="border border-border bg-card p-4" data-testid="import-health-bar">
      <div className="flex items-center justify-between mb-2">
        <div className="label-caps">Live Import Health</div>
        {summary.estimated_fix_minutes > 0 && (
          <div className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid="estimated-fix-time">
            <Clock className="h-3.5 w-3.5" /> Estimated fix time: ~{summary.estimated_fix_minutes} min
          </div>
        )}
      </div>
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-secondary">
        {ready > 0 && <div className="bg-[#16A34A] transition-all duration-500" style={{ width: pct(ready) }} title={`Ready: ${ready}`} />}
        {warn > 0 && <div className="bg-amber-500 transition-all duration-500" style={{ width: pct(warn) }} title={`Warnings: ${warn}`} />}
        {err > 0 && <div className="bg-destructive transition-all duration-500" style={{ width: pct(err) }} title={`Errors: ${err}`} />}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#16A34A]" /> {fmtNum(ready)} ready</span>
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500" /> {fmtNum(warn)} warning{warn === 1 ? "" : "s"}</span>
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-destructive" /> {fmtNum(err)} error{err === 1 ? "" : "s"}</span>
      </div>
    </div>
  );
}

function DuplicateAnalysis({ dup }) {
  const items = [
    { icon: UserPlus, label: "New Accounts", value: dup.new_accounts, accent: "text-[#16A34A]", sev: "ok", testId: "dup-new" },
    { icon: Users, label: "Existing Accounts", value: dup.existing_accounts, accent: "text-amber-600", sev: "warn", testId: "dup-existing" },
    { icon: Phone, label: "Duplicate Mobiles", value: dup.duplicate_mobiles, accent: "text-amber-600", sev: "warn", testId: "dup-mobile" },
    { icon: Fingerprint, label: "Duplicate Loan IDs", value: dup.duplicate_loan_ids, accent: "text-destructive", sev: "err", testId: "dup-loanid" },
  ];
  return (
    <div className="border border-border bg-card p-4" data-testid="duplicate-analysis">
      <div className="label-caps mb-3">Smart Duplicate Analysis</div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map((it) => (
          <div key={it.label} className="flex items-center gap-3 border border-border rounded-sm p-3 bg-secondary/30" data-testid={it.testId}>
            <div className={`h-9 w-9 rounded-sm flex items-center justify-center shrink-0 ${it.sev === "err" ? "bg-destructive/10" : it.sev === "warn" ? "bg-amber-500/10" : "bg-[#16A34A]/10"}`}>
              <it.icon className={`h-4 w-4 ${it.accent}`} strokeWidth={2.2} />
            </div>
            <div>
              <div className={`text-xl font-black font-heading mono leading-none ${it.accent}`}>{fmtNum(it.value)}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{it.label}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ErrorCategoryGroups({ categories }) {
  if (!categories.length) return null;
  return (
    <div className="space-y-2" data-testid="error-category-groups">
      {categories.map((c) => (
        <div key={c.category} className={`border rounded-sm p-3 ${c.severity === "error" ? "border-destructive/30 bg-destructive/5" : "border-amber-200 bg-amber-50"}`} data-testid={`category-${c.category}`}>
          <div className="flex items-center gap-2">
            {c.severity === "error" ? <XCircle className="h-4 w-4 text-destructive shrink-0" /> : <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />}
            <span className={`text-sm font-semibold ${c.severity === "error" ? "text-destructive" : "text-amber-800"}`}>{c.title}</span>
            <span className="text-xs text-muted-foreground">· {c.count} row{c.count === 1 ? "" : "s"}</span>
          </div>
          <div className="text-xs text-muted-foreground mt-1 pl-6">{c.explanation} <span className="font-medium text-foreground">{c.suggested_action}</span></div>
          <div className="text-xs text-muted-foreground mt-1 pl-6">
            Rows: {c.rows.slice(0, 25).join(", ")}{c.count > c.rows.length ? ` … +${c.count - c.rows.length} more` : ""}
          </div>
        </div>
      ))}
    </div>
  );
}

function FixRow({ finding, onSave, onApplyFix, busy }) {
  const [value, setValue] = useState(finding.current_value);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(finding.current_value); }, [finding.id, finding.current_value]);
  const dirty = value !== finding.current_value;
  const save = async () => {
    setSaving(true);
    try { await onSave([{ row_index: finding.row_index, field_key: finding.field_key, value }]); }
    finally { setSaving(false); }
  };
  return (
    <tr className="border-b border-border last:border-0 align-top" data-testid={`fix-row-${finding.id}`}>
      <td className="p-2 mono text-muted-foreground">{finding.row}</td>
      <td className="p-2 font-medium whitespace-nowrap">{finding.field_label}</td>
      <td className="p-2">
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && dirty) save(); }}
          className="w-full min-w-[120px] rounded-sm border border-border bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#0B3D91]"
          data-testid={`fix-input-${finding.id}`}
        />
      </td>
      <td className="p-2">
        <span className={`inline-flex items-center gap-1 ${finding.severity === "error" ? "text-destructive" : "text-amber-700"}`}>
          {finding.severity === "error" ? <XCircle className="h-3.5 w-3.5 shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 shrink-0" />}
          {finding.issue}
        </span>
        {finding.detail?.existing_name && <div className="text-[11px] text-muted-foreground mt-0.5">Existing: {finding.detail.existing_name}</div>}
      </td>
      <td className="p-2 text-muted-foreground">{finding.suggested_fix}</td>
      <td className="p-2 whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          {dirty ? (
            <Button size="sm" className="h-7 rounded-sm gap-1 bg-[#16A34A] hover:bg-[#138a3e] text-xs px-2" onClick={save} disabled={saving || busy} data-testid={`fix-save-${finding.id}`}>
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="h-3 w-3" /> Save</>}
            </Button>
          ) : finding.autofix_value !== undefined ? (
            <Button size="sm" variant="outline" className="h-7 rounded-sm gap-1 text-xs px-2 border-[#0B3D91]/40 text-[#0B3D91]" onClick={() => onApplyFix(finding)} disabled={busy} data-testid={`fix-apply-${finding.id}`}>
              <Wand2 className="h-3 w-3" /> Fix
            </Button>
          ) : (
            <span className={`inline-flex items-center gap-1 text-xs ${finding.severity === "error" ? "text-destructive" : "text-amber-600"}`}>
              {finding.severity === "error" ? "Needs fix" : "Optional"}
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

function RowFixGrid({ findings, onSave, onApplyFix, busy }) {
  const [filter, setFilter] = useState("all");
  const shown = findings.filter((f) => filter === "all" || f.severity === filter);
  if (!findings.length) {
    return (
      <div className="border border-[#16A34A]/30 bg-[#16A34A]/5 rounded-sm p-6 text-center" data-testid="fix-grid-empty">
        <CheckCircle2 className="h-8 w-8 text-[#16A34A] mx-auto" />
        <div className="text-sm font-semibold text-[#138a3e] mt-2">No issues found — every row passed validation.</div>
      </div>
    );
  }
  const Btn = ({ id, label }) => (
    <button type="button" onClick={() => setFilter(id)}
      className={`px-2.5 py-1 rounded-sm text-xs font-semibold border transition-colors ${filter === id ? "bg-[#0B3D91] text-white border-[#0B3D91]" : "bg-card text-muted-foreground border-border hover:border-[#0B3D91]/40"}`}
      data-testid={`fix-filter-${id}`}>{label}</button>
  );
  return (
    <div className="border border-border bg-card" data-testid="row-fix-grid">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b border-border">
        <div className="text-sm font-semibold">Fix issues inline ({shown.length})</div>
        <div className="flex items-center gap-1.5">
          <Btn id="all" label="All" /><Btn id="error" label="Errors" /><Btn id="warning" label="Warnings" />
        </div>
      </div>
      <div className="overflow-x-auto max-h-[460px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10">
            <tr className="label-caps bg-secondary/60 border-b border-border">
              <th className="text-left p-2">Row</th>
              <th className="text-left p-2">Field</th>
              <th className="text-left p-2">Current Value</th>
              <th className="text-left p-2">Issue</th>
              <th className="text-left p-2">Suggested Fix</th>
              <th className="text-left p-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((f) => <FixRow key={f.id} finding={f} onSave={onSave} onApplyFix={onApplyFix} busy={busy} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ValidateStep({
  validation, validating, busy, dedupe, onDedupe, saveMapping, setSaveMapping,
  onBack, onImport, onEdit, onApplyFix, onAutofix, onReplaceFile, onDownloadReport,
}) {
  const replaceRef = useRef(null);
  if (validating && !validation) return <ScanningState />;
  if (!validation) return <ScanningState />;
  const { summary, duplicate_analysis, categories, findings, missing_columns, can_import } = validation;
  const hasAutofixes = findings.some((f) => f.autofix_value !== undefined);
  const isUpdate = dedupe === "update";

  return (
    <div className="space-y-4" data-testid="import-validate-step">
      <ReassuringBanner summary={summary} />
      <HealthSummary summary={summary} />
      <HealthBar summary={summary} />
      <DuplicateAnalysis dup={duplicate_analysis} />

      {/* Canonical DPD preview (calculated on import from Payment Due Date) */}
      {validation.dpd_calculated_preview != null && (
        <div className="border border-[#0B3D91]/20 bg-[#0B3D91]/5 rounded-sm p-3" data-testid="dpd-preview-card">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#0B3D91]"><Wand2 className="h-4 w-4" /> Canonical DPD preview</div>
          <div className="text-xs text-muted-foreground mt-1">
            RecoverOS will auto-calculate DPD from each Payment Due Date on import:
            <span className="ml-1 font-semibold text-foreground">{validation.dpd_calculated_preview} calculated</span>
            {validation.dpd_unknown_preview > 0 && <> · <span className="font-semibold text-amber-700">{validation.dpd_unknown_preview} Unknown</span> (missing/invalid Payment Due Date)</>}.
          </div>
        </div>
      )}

      {missing_columns?.length > 0 && (
        <div className="border border-destructive/30 bg-destructive/5 rounded-sm p-3" data-testid="missing-columns-warning">
          <div className="flex items-center gap-2 text-sm font-semibold text-destructive"><XCircle className="h-4 w-4" /> Mandatory columns not mapped</div>
          <div className="text-xs text-muted-foreground mt-1">Please go back and map: <span className="font-medium text-foreground">{missing_columns.join(", ")}</span></div>
        </div>
      )}

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2">
        {hasAutofixes && (
          <Button variant="outline" className="rounded-sm gap-1.5 border-[#0B3D91]/40 text-[#0B3D91]" onClick={onAutofix} disabled={busy} data-testid="autofix-all-btn">
            <Wand2 className="h-4 w-4" /> Fix all automatically
          </Button>
        )}
        <input ref={replaceRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => { onReplaceFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="replace-file-input" />
        <Button variant="outline" className="rounded-sm gap-1.5" onClick={() => replaceRef.current?.click()} disabled={busy} data-testid="replace-file-btn">
          <Replace className="h-4 w-4" /> Replace Excel file
        </Button>
        {(summary.error_count > 0 || summary.warning_count > 0) && (
          <Button variant="outline" className="rounded-sm gap-1.5" onClick={onDownloadReport} disabled={busy} data-testid="download-error-report-btn">
            <FileDown className="h-4 w-4" /> Download error report (Excel)
          </Button>
        )}
      </div>

      <ErrorCategoryGroups categories={categories} />
      <RowFixGrid findings={findings} onSave={onEdit} onApplyFix={onApplyFix} busy={busy} />

      {/* Existing-account handling (preserves the original refresh/skip flow) */}
      {duplicate_analysis.existing_accounts > 0 && (
        <div className="border border-border bg-card p-4" data-testid="dedupe-mode">
          <div className="text-sm font-semibold mb-2">For {fmtNum(duplicate_analysis.existing_accounts)} account{duplicate_analysis.existing_accounts === 1 ? "" : "s"} that already exist (matched by Loan ID):</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <button type="button" onClick={() => onDedupe("skip")} disabled={busy}
              className={`text-left rounded-sm border p-3 transition-colors ${dedupe === "skip" ? "border-[#0B3D91] ring-1 ring-[#0B3D91] bg-[#0B3D91]/5" : "border-border hover:border-[#0B3D91]/40"}`}
              data-testid="dedupe-skip">
              <div className="flex items-center gap-1.5 font-semibold text-sm"><Check className={`h-4 w-4 ${dedupe === "skip" ? "text-[#0B3D91]" : "text-transparent"}`} /> Skip them</div>
              <div className="text-xs text-muted-foreground mt-1 pl-5">Only create brand-new accounts. Leave existing ones untouched.</div>
            </button>
            <button type="button" onClick={() => onDedupe("update")} disabled={busy}
              className={`text-left rounded-sm border p-3 transition-colors ${isUpdate ? "border-[#16A34A] ring-1 ring-[#16A34A] bg-[#16A34A]/5" : "border-border hover:border-[#16A34A]/40"}`}
              data-testid="dedupe-update">
              <div className="flex items-center gap-1.5 font-semibold text-sm"><RefreshCw className={`h-4 w-4 ${isUpdate ? "text-[#16A34A]" : "text-muted-foreground"}`} /> Refresh balances</div>
              <div className="text-xs text-muted-foreground mt-1 pl-5">Update outstanding, DPD & other mapped fields on existing accounts.</div>
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button variant="outline" className="rounded-sm gap-1" onClick={onBack} disabled={busy}><ArrowLeft className="h-4 w-4" /> Back</Button>
        <div className="flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="save-mapping-toggle">
            <input type="checkbox" checked={saveMapping} onChange={(e) => setSaveMapping(e.target.checked)} className="h-4 w-4 accent-[#0B3D91]" />
            <span className="inline-flex items-center gap-1"><Save className="h-3.5 w-3.5 text-muted-foreground" /> Save this mapping for next time</span>
          </label>
          {!can_import && (
            <span className="text-xs text-destructive font-medium" data-testid="import-blocked-msg">Resolve the remaining errors to continue.</span>
          )}
          <Button className="rounded-sm gap-1 bg-[#16A34A] hover:bg-[#138a3e]" onClick={onImport} disabled={busy || !can_import} data-testid="import-confirm-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Import {fmtNum(summary.importable_rows)} Accounts <ArrowRight className="h-4 w-4" /></>}
          </Button>
        </div>
      </div>
    </div>
  );
}
